import { useCallback, useEffect, useState } from 'react';

import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { createContainer, useContainer } from 'unstated-next';

import { DELETE_LTI_CREDENTIAL, GET_LTI_CREDENTIAL_LIST, REGENERATE_SHARED_SECRET, UPSERT_LTI_CREDENTIAL } from '../../config/apiPaths';
import { LTICredential } from '../../types/apiTypes';
import { getUrls } from '../../utils/url';
import AdminAuthContainer from './AdminAuthContainer';
import { getUserUuidHeaders } from '../../utils/httpUtils';

enum IntegrationsView {
    List = 0,
    Edit = 1,
    Create = 2
}

enum LTIVersion {
    V1_1 = '1.1',
    V1_3 = '1.3'
}

const useAdminIntegrations = () => {
    const { uuid, isLoading } = useContainer(AdminAuthContainer);
    const { api } = getUrls();
    const { t } = useTranslation('admin_settings');
    const [integrations, setIntegrations] = useState<LTICredential[]>([]);
    const [integrationsFetchStatus, setIntegrationsFetchStatus] = useState<boolean>(false);
    const [selectedIntegration, setSelectedIntegration] = useState<any>(null);
    const [view, setView] = useState<IntegrationsView>(IntegrationsView.List);

    const { enqueueSnackbar } = useSnackbar();

    const fetchIntegrationList = useCallback(
        async () => {
            try {
                const { data }: { data: LTICredential[] } = await axios.get(`${api}${GET_LTI_CREDENTIAL_LIST}`, { headers: getUserUuidHeaders(uuid) });
                setIntegrations(data);
                setIntegrationsFetchStatus(true);
            } catch (err) {
                console.log(err);
                enqueueSnackbar(t('integration-error.fetch-all-credentials'));
            }
        }, [api, uuid, t, enqueueSnackbar]
    );

    const updateIntegration = async (recordData: Partial<LTICredential>) => {
        try {
            const { data }: { data: LTICredential } = await axios.post(`${api}${UPSERT_LTI_CREDENTIAL}`, {
                ...recordData,
                user_uuid: uuid
            });
            const successMessage = t('integration-success.update', { title: recordData.title });
            enqueueSnackbar(successMessage, { variant: 'success' });
            return data;
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('integration-error.update-integration'));
        }
    };

    const createIntegration = async (recordData: Partial<LTICredential>) => {
        try {
            const { data }: { data: LTICredential } = await axios.post(`${api}${UPSERT_LTI_CREDENTIAL}`, {
                user_uuid: uuid,
                ...recordData
            });
            const successMessage = t('integration-success.create', { title: recordData.title });
            enqueueSnackbar(successMessage, { variant: 'success' });
            return data;
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('integration-error.create-integration'));
        }
    };

    const regenerateSecret = async () => {
        try {
            const { data } = await axios.post(`${api}${REGENERATE_SHARED_SECRET}`, {
                lti_creds_uuid: selectedIntegration?.lti_creds_uuid,
                user_uuid: uuid
            });
            return data;
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('integration-error.regenerate-secret'));
        }
    };

    const deleteIntegration = async () => {
        try {
            const { data } = await axios.delete(`${api}${DELETE_LTI_CREDENTIAL}?lti_creds_uuid=${selectedIntegration?.lti_creds_uuid}`, { headers: getUserUuidHeaders(uuid) });
            return data;
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('integration-error.delete-integration'));
        }
    };

    useEffect(() => {
        if (!isLoading && uuid) {
            fetchIntegrationList();
        }
    }, [view, fetchIntegrationList, isLoading, uuid]);

    return {
        fetchIntegrationList,
        integrations,
        integrationsFetchStatus,
        selectedIntegration,
        setSelectedIntegration,
        updateIntegration,
        createIntegration,
        deleteIntegration,
        regenerateSecret,
        IntegrationsView,
        LTIVersion,
        view,
        setView,
        uuid
    };
};

const AdminIntegrationsContainer = createContainer(useAdminIntegrations);

export default AdminIntegrationsContainer;
